import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { apiFetch } from '../lib/api';

// ── Types ───────────────────────────────────────────────────────────────────

type EUSize = 'S' | 'M' | 'L' | 'XL';

interface EUSizeStats {
  count: number;
  avgAccuracyPercent: number;
}

interface CalibrationData {
  insufficientData?: boolean;
  message?: string;
  totalSessions: number;
  overallAccuracyPercent?: number;
  accurateCount?: number;
  byEUSize?: Partial<Record<EUSize, EUSizeStats>>;
  recommendations?: string[];
}

interface CalibrationCardProps {
  projectId: string;
  cardCls?: string;
  cardStyle?: React.CSSProperties;
  SectionHeader: React.ComponentType<{ color: string; children: React.ReactNode }>;
}

// ── Component ───────────────────────────────────────────────────────────────

export default function CalibrationCard({ projectId, cardCls, cardStyle: outerCardStyle, SectionHeader }: CalibrationCardProps) {
  const [data, setData] = useState<CalibrationData | null>(null);

  useEffect(() => {
    apiFetch<CalibrationData>(
      `/projects/${projectId}/phases/phase_4_development/execute/calibration-report`,
    )
      .then(setData)
      .catch(() => {});
  }, [projectId]);

  if (!data) return null;

  // Insufficient data state
  if (data.insufficientData) {
    return (
      <div className={cardCls} style={{ ...outerCardStyle, border: '1px solid rgba(139,92,246,0.15)', background: 'rgba(139,92,246,0.02)' }}>
        <SectionHeader color="#8b5cf6">Estimation Calibration</SectionHeader>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ fontSize: 20 }}>📊</span>
          <div style={{ flex: 1 }}>
            <p style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text-secondary)', marginBottom: 8 }}>
              Available after {data.totalSessions}/3 completed sessions
            </p>
            <div style={{ width: '100%', maxWidth: 160, background: 'var(--border)', borderRadius: 4, height: 4 }}>
              <div style={{
                width: `${Math.min((data.totalSessions / 3) * 100, 100)}%`,
                background: '#8b5cf6', borderRadius: 4, height: 4, transition: 'width 0.3s',
              }} />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (data.overallAccuracyPercent == null) return null;

  const accuracyColor = data.overallAccuracyPercent >= 75 ? '#22c55e'
    : data.overallAccuracyPercent >= 50 ? '#eab308'
    : '#ef4444';

  const showRecommendation = data.recommendations
    && data.recommendations.length > 0
    && data.recommendations[0] !== 'Estimation accuracy is within acceptable range. Continue monitoring.';

  return (
    <div className={cardCls} style={{ ...outerCardStyle, border: '1px solid rgba(139,92,246,0.3)', background: 'rgba(139,92,246,0.04)' }}>
      <SectionHeader color="#8b5cf6">Estimation Calibration</SectionHeader>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
        <span style={{ fontSize: 24 }}>📊</span>
        <div style={{ flex: 1, minWidth: 0 }}>
          {/* Stats row */}
          <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
            <div>
              <p style={{ fontFamily: 'var(--font-body)', fontSize: 22, fontWeight: 700, color: accuracyColor }}>
                {data.overallAccuracyPercent.toFixed(0)}%
              </p>
              <p style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--text-secondary)' }}>Overall accuracy</p>
            </div>
            <div>
              <p style={{ fontFamily: 'var(--font-body)', fontSize: 22, fontWeight: 700, color: 'var(--text)' }}>
                {data.totalSessions}
              </p>
              <p style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--text-secondary)' }}>Sessions analysed</p>
            </div>
            <div>
              <p style={{ fontFamily: 'var(--font-body)', fontSize: 22, fontWeight: 700, color: 'var(--text)' }}>
                {data.accurateCount ?? 0}
              </p>
              <p style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--text-secondary)' }}>Accurate estimates</p>
            </div>
          </div>

          {/* EU size bars */}
          {data.byEUSize && (
            <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 4 }}>
              {(['S', 'M', 'L', 'XL'] as EUSize[]).map(size => {
                const stats = data.byEUSize?.[size];
                if (!stats || stats.count === 0) return null;
                const pct = stats.avgAccuracyPercent;
                const barColor = pct >= 75 ? '#22c55e' : pct >= 50 ? '#eab308' : '#ef4444';
                return (
                  <div key={size} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--text-secondary)', width: 20 }}>{size}</span>
                    <div style={{ flex: 1, background: 'var(--border)', borderRadius: 3, height: 5 }}>
                      <div style={{ width: `${Math.min(pct, 100)}%`, background: barColor, borderRadius: 3, height: 5 }} />
                    </div>
                    <span style={{ fontFamily: 'var(--font-body)', fontSize: 11, color: 'var(--text-secondary)', width: 32, textAlign: 'right' }}>
                      {pct.toFixed(0)}%
                    </span>
                    <span style={{ fontFamily: 'var(--font-body)', fontSize: 11, color: 'var(--text-secondary)', width: 24, textAlign: 'right' }}>
                      ({stats.count})
                    </span>
                  </div>
                );
              })}
            </div>
          )}

          {/* Recommendation */}
          {showRecommendation && (
            <p style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: '#eab308', marginTop: 10 }}>
              ⚠ {data.recommendations![0]}
            </p>
          )}

          {/* Link to full report */}
          <Link
            to={`/projects/${projectId}/calibration`}
            style={{
              display: 'inline-block', marginTop: 12,
              fontFamily: 'var(--font-body)', fontSize: 13, fontWeight: 600,
              color: '#8b5cf6', textDecoration: 'none',
            }}
          >
            View full report →
          </Link>
        </div>
      </div>
    </div>
  );
}
